/**
 * Author: Andrey Morozov
 * Confidential. Proprietary code. Do not distribute without authorization.
 *
 * usePresenceHeartbeat.ts — us\1 \2resenc\1 \2eartbeat (services/operator-web-app).
 */

import { useEffect, useRef } from 'react';
import { useOperatorStore } from '@/store';
import { buildPresenceHeartbeat } from '@/realtime/commands';

const APP_START_MS = typeof performance !== 'undefined' ? performance.timeOrigin : Date.now();

/**
 * Sends presence_heartbeat at server-configured interval when connected and registered.
 * Clears interval on disconnect or offline.
 */
export function usePresenceHeartbeat(): void {
  const connectionState = useOperatorStore((s) => s.connectionState);
  const presence = useOperatorStore((s) => s.presence);
  const send = useOperatorStore((s) => s.send);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    const shouldHeartbeat =
      connectionState === 'connected' &&
      presence.heartbeatIntervalMs > 0 &&
      presence.status !== 'offline';

    if (!shouldHeartbeat) return;

    const tick = () => {
      const uptime_ms = Math.round(Date.now() - APP_START_MS);
      const tab_visible =
        typeof document !== 'undefined' ? document.visibilityState === 'visible' : true;
      send(buildPresenceHeartbeat({ uptime_ms, tab_visible }));
    };

    tick();
    intervalRef.current = setInterval(tick, presence.heartbeatIntervalMs);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [connectionState, presence.heartbeatIntervalMs, presence.status, send]);
}
